import { useCallback } from "react";

import i18next from "i18next";

import { isProduction } from "utilities/utils";

import { Group, Namespace } from "../localization/model";

const DEBUG = !isProduction && localStorage.getItem("i18nextDebug") === "true";

const regex = /^[A-Z0-9_]+\.[a-zA-Z0-9]*$/;

export { Group, Namespace };

export type Options = Record<string, string | number | boolean | undefined> & {
  group?: Group;
  namespace?: Namespace;
  upperCase?: boolean;
};

export type Translator = (key: string, options?: Options) => string;

export interface UseTranslate {
  translate: Translator;
}

export const getId = (
  key: string,
  group?: Group,
  namespace?: Namespace
): string => `${namespace}:${group}.${key}`;

export const useTranslate = (
  namespace: Namespace = Namespace.DEFAULT,
  group: Group = Group.DEFAULT
): UseTranslate => {
  const defaultNamespace = namespace;
  const defaultGroup = group;

  const translateId = useCallback(
    (key: string, group?: Group, namespace?: Namespace): string =>
      getId(key, group || defaultGroup, namespace || defaultNamespace),
    [defaultGroup, defaultNamespace]
  );

  /**
   * Provides a translation for a given key
   *
   * @param key - The key in the translation file
   * @param options - Options for the translation:
   * @param options.placeholders - Object of key/value pairs { key1: "value1", key2: "value2" } to be replaced in the translation. The placeholder should be identified with double curly-braces: Replace {{key1}} and {{key2}} in the json file.
   * @param options.group - The group in the translation file (default is group used to create the hook, or Group.DEFAULT if none is provided)
   * @param options.namespace - The namespace in the translation file (default is namespace used to create the hook, or Namespace.DEFAULT if none is provided)
   * If a namespace is provided here, but no group, the default group will be used.
   */

  const translate = useCallback(
    (key: string, options?: Options): string => {
      let result = i18next.t(
        translateId(
          key,
          !!options?.namespace && !options.group
            ? Group.DEFAULT
            : options?.group,
          options?.namespace
        ),
        options ?? {}
      );

      if (options?.upperCase) {
        result = result.toUpperCase();
      }

      if (DEBUG) {
        const debugSymbol = regex.test(result) ? "❌" : "✅";
        const debugKey = key.split(".").slice(-1)[0] || "";
        const res = `${
          debugKey.startsWith("date") ? result : debugKey
        }${debugSymbol}`;
        return "returnObjects" in (options ?? {})
          ? ([res] as unknown as string)
          : res;
      }

      return result;
    },
    [translateId]
  );

  return {
    translate,
  };
};
